/* MessagingStreamProvider — ONE shared SSE connection for the whole app.

   Before this provider existed, UnreadContext, CallContext, and
   MessageRoom each opened their own EventSource to /api/messaging/stream.
   That meant 3 simultaneous SSE connections per signed-in user, which on
   iOS PWA + Safari hits browser per-host connection limits, gets
   throttled by the cluster ingress, and stalls both messages and call
   signaling. With one stream, all consumers subscribe via
   `useMessagingStream(handler)` and the same JSON event is fanned out to
   every subscriber in-process. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { openMessagingStream } from "../lib/messagingApi";
import { useUser } from "./UserAuthContext";

const StreamCtx = createContext(null);

export function MessagingStreamProvider({ children }) {
  const { user } = useUser();
  const subsRef = useRef(new Set()); // Set<(event) => void>
  const stopRef = useRef(null);
  // Exposes the raw SSE lifecycle to UI:
  //   "idle" (not signed in) → "connecting" → "open" ↔ "reconnecting" → "unauthenticated"|"idle"
  // The MessagesList header uses this to show a small green/red dot so
  // the user knows whether new messages will arrive live.
  const [status, setStatus] = useState("idle");

  // Open exactly one SSE while a user is signed in. Reconnects are handled
  // inside `openMessagingStream`.
  useEffect(() => {
    if (!user?.id) {
      if (stopRef.current) { stopRef.current(); stopRef.current = null; }
      setStatus("idle");
      return undefined;
    }
    setStatus("connecting");
    const stop = openMessagingStream(
      (ev) => {
        // Fan-out to every active subscriber. We copy to avoid mutation
        // during iteration (subscribers may unsubscribe in response to ev).
        for (const fn of Array.from(subsRef.current)) {
          try { fn(ev); } catch { /* noop — one bad handler doesn't break others */ }
        }
      },
      (next) => setStatus(next),
    );
    stopRef.current = stop;
    return () => { stop(); stopRef.current = null; };
  }, [user?.id]);

  const subscribe = useCallback((handler) => {
    subsRef.current.add(handler);
    return () => { subsRef.current.delete(handler); };
  }, []);

  const value = useMemo(() => ({ subscribe, status }), [subscribe, status]);
  return <StreamCtx.Provider value={value}>{children}</StreamCtx.Provider>;
}

/* Convenience hook: register a handler, auto-unsubscribe on unmount. */
export function useMessagingStream(handler) {
  const ctx = useContext(StreamCtx);
  const ref = useRef(handler);
  useEffect(() => { ref.current = handler; }, [handler]);

  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.subscribe((ev) => ref.current?.(ev));
  }, [ctx]);
}

/* Live SSE connection status — one of "idle" | "connecting" | "open" |
 * "reconnecting" | "unauthenticated". Renders 3 times per lifecycle
 * change, otherwise never re-renders (context value memoised above). */
export function useMessagingStreamStatus() {
  const ctx = useContext(StreamCtx);
  return ctx?.status || "idle";
}
